import React, { useEffect, useMemo, useState } from 'react';
import type { KryspaceControlBridgeStructure } from '../../../types/kryspaceControlBridgeStructure';
import type { ControlBridgeViewModel } from '../deriveViewModel';
import type { ControlBridgeDetailSelection } from '../ControlBridgeDetailPanel';
import { deriveDiagnoseScopeHealthRows, type DiagnoseScopeHealthRow } from '../deriveDiagnoseHealth';
import type { ResourceFilter, ScopeContext } from '../filters';
import { passesFilter } from '../filters';
import type { ControlBridgeQueueItem } from '../deriveViewModel';
import { sortByPriority } from '../queueHelpers';
import { AlertBanner, FilterBar, NotAssessed, SectionLabel } from '../shared/SurfaceParts';
import type { PersonalLayerUserContext } from '../personalLayer/buildPersonalLayerViewModels';
import {
  buildPersonalLoadLeverage,
  buildPrimaryConstraint,
  diagnoseResourcePosition,
  diagnoseThreeQuestions,
  entropyBarTone,
  healthBarFillPct,
  scopedP0Issues,
  sortDiagnoseRowsWorstLast,
  subdomainBlockingLabel,
  vgrComponentFillPct,
} from './diagnoseHelpers';
import { IssuesInbox } from '../issues/IssuesInbox';
import { deriveVgrHistory } from './diagnoseVgrHistory';
import { DiagnoseVgrHistoryChart } from './DiagnoseVgrHistoryChart';
import {
  bridgeSessionKindForSurface,
  bridgeSessionKindLabel,
} from '../bridgeSessionKind';
import { entropyStateLabel } from '../entropyLanguage';
import styles from '../ControlBridge.module.css';

export interface DiagnosePersonalLayerProps {
  vm: ControlBridgeViewModel;
  structure: KryspaceControlBridgeStructure;
  scope: string;
  resource: ResourceFilter;
  scopeContext?: ScopeContext;
  scopeLabel: string;
  resourceLabel: string;
  onClearResource?: () => void;
  onNavigate?: (surface: string) => void;
  onSelectDetail?: (selection: ControlBridgeDetailSelection) => void;
  personalLayerUser?: PersonalLayerUserContext;
  workspaceSlug?: string;
  issuesFocusItemId?: string | null;
  issuesRefreshToken?: number;
  issuesListOpen?: boolean;
  onOpenIssueItem?: (itemId: string, label: string) => void;
}

function filterScopedQueue(
  items: ControlBridgeQueueItem[],
  scope: string,
  resource: ResourceFilter,
  scopeContext?: ScopeContext
): ControlBridgeQueueItem[] {
  return sortByPriority(items.filter((item) => passesFilter(item, scope, resource, scopeContext)));
}

function EntropyBar({
  entropy,
  label,
}: {
  entropy: number | null;
  label: string;
}) {
  const fill = healthBarFillPct(entropy);
  const tone = entropyBarTone(entropy);
  return (
    <div className={styles.mcProgressWrap} role="img" aria-label={label}>
      <div className={styles.mcProgressTrack}>
        <div
          className={`${styles.mcProgressFill} ${styles[`mcProgressFill_${tone}`]}`}
          style={{ width: `${fill}%` }}
        />
      </div>
    </div>
  );
}

function FramingLines({
  objective,
  needsFixing,
  leverage,
}: {
  objective: string;
  needsFixing: string;
  leverage: string;
}) {
  return (
    <dl className={styles.mcFraming}>
      <div className={styles.mcFramingRow}>
        <dt>Objective</dt>
        <dd>{objective}</dd>
      </div>
      <div className={styles.mcFramingRow}>
        <dt>Needs fixing</dt>
        <dd>{needsFixing}</dd>
      </div>
      <div className={styles.mcFramingRow}>
        <dt>Leverage</dt>
        <dd>{leverage}</dd>
      </div>
    </dl>
  );
}

export const DiagnosePersonalLayer: React.FC<DiagnosePersonalLayerProps> = ({
  vm,
  structure,
  scope,
  resource,
  scopeContext,
  scopeLabel,
  resourceLabel,
  onClearResource,
  onNavigate,
  personalLayerUser,
  workspaceSlug,
  issuesFocusItemId,
  issuesRefreshToken = 0,
  issuesListOpen = false,
  onOpenIssueItem,
}) => {
  const [expandedScopeId, setExpandedScopeId] = useState<string | null>(null);
  const [trendScopeId, setTrendScopeId] = useState<string | null>(null);
  const [issuesOpen, setIssuesOpen] = useState(issuesListOpen);

  useEffect(() => {
    if (issuesListOpen) setIssuesOpen(true);
  }, [issuesListOpen]);

  const scopedIssues = useMemo(
    () => filterScopedQueue(vm.openIssues, scope, resource, scopeContext),
    [vm.openIssues, scope, resource, scopeContext]
  );
  const scopedWork = useMemo(
    () => filterScopedQueue(vm.eligibleWork, scope, resource, scopeContext),
    [vm.eligibleWork, scope, resource, scopeContext]
  );
  const rows = useMemo(
    () =>
      sortDiagnoseRowsWorstLast(
        deriveDiagnoseScopeHealthRows(structure, scope, vm, scopedIssues, scopedWork, scopeContext)
      ),
    [structure, scope, vm, scopedIssues, scopedWork, scopeContext]
  );

  const p0IssueItems = useMemo(() => scopedP0Issues(scopedIssues), [scopedIssues]);
  const primaryConstraint = useMemo(
    () => buildPrimaryConstraint(rows, scopedIssues, scopedWork),
    [rows, scopedIssues, scopedWork]
  );
  const personalMetrics = useMemo(
    () => buildPersonalLoadLeverage(scopedIssues, scopedWork, personalLayerUser),
    [scopedIssues, scopedWork, personalLayerUser]
  );

  const resourcePosition = useMemo(() => diagnoseResourcePosition(rows), [rows]);

  const toggleRow = (row: DiagnoseScopeHealthRow) => {
    setExpandedScopeId((prev) => (prev === row.scopeId ? null : row.scopeId));
  };

  return (
    <div className={styles.surfaceStackWide} data-testid="diagnose-personal-layer">
      <FilterBar
        scopeLabel={scopeLabel}
        resourceLabel={resourceLabel}
        onClearResource={onClearResource}
        sessionKindLabel={bridgeSessionKindLabel(bridgeSessionKindForSurface('diagnose')!)}
      />

      {p0IssueItems.length > 0 ? (
        <AlertBanner
          title={`${p0IssueItems.length} P0 issue${p0IssueItems.length === 1 ? '' : 's'} in scope`}
        />
      ) : null}

      <section aria-label="Issue statistics">
        <SectionLabel>Issues</SectionLabel>
        <button
          type="button"
          className={styles.diagIssueSummaryBtn}
          data-testid="diagnose-issues-summary"
          aria-expanded={issuesOpen}
          onClick={() => setIssuesOpen((open) => !open)}
        >
          <span className={styles.diagIssueSummaryCount}>
            {scopedIssues.length} open issue{scopedIssues.length === 1 ? '' : 's'} in scope
          </span>
          <span>{issuesOpen ? 'Hide list' : 'Show list'}</span>
        </button>
        {issuesOpen && workspaceSlug ? (
          <IssuesInbox
            workspaceSlug={workspaceSlug}
            focusItemId={issuesFocusItemId}
            refreshToken={issuesRefreshToken}
            onOpenItem={onOpenIssueItem ?? ((_id, _label) => undefined)}
            hideItemIds
          />
        ) : null}
        {issuesOpen && !workspaceSlug ? (
          scopedIssues.length > 0 ? (
            <ul data-testid="diagnose-issues-scoped-fallback">
              {scopedIssues.map((item) => (
                <li key={item.itemId} className={styles.embeddedListItem}>
                  <span className={styles.embeddedListTitle}>{item.title}</span>
                </li>
              ))}
            </ul>
          ) : (
            <NotAssessed label="Open issues in this scope" />
          )
        ) : null}
      </section>

      <section aria-label="System entropy by subdomain">
        <SectionLabel>System — entropy by subdomain</SectionLabel>
        {rows.length === 0 ? (
          <NotAssessed label="Subdomain health and VGR" />
        ) : (
          <div className={styles.mcGlanceGrid} role="list">
            {rows.map((row) => {
              const expanded = expandedScopeId === row.scopeId;
              const framing = diagnoseThreeQuestions(row);
              return (
                <div key={row.scopeId} className={styles.flowGridCell} role="listitem">
                  <button
                    type="button"
                    className={`${styles.mcGlanceTile} ${expanded ? styles.mcGlanceTileActive : ''}`}
                    onClick={() => toggleRow(row)}
                    aria-expanded={expanded}
                  >
                    <span className={styles.mcGlanceLabel}>{row.label}</span>
                    {entropyStateLabel(row.entropy) ? (
                      <span className={styles.mcGlanceMetric}>{entropyStateLabel(row.entropy)}</span>
                    ) : row.entropy != null ? (
                      <EntropyBar
                        entropy={row.entropy}
                        label={`${row.label} entropy ${row.entropy}`}
                      />
                    ) : (
                      <span className={styles.mcGlanceMetric}>{row.label} no target</span>
                    )}
                    <span className={styles.mcGlanceMetric}>
                      {entropyStateLabel(row.entropy) ??
                        (row.entropy != null ? String(row.entropy) : 'no target')}
                      {entropyStateLabel(row.entropy) ? null : (
                        <span className={styles.mcGlanceUnit}> entropy</span>
                      )}
                    </span>
                    <span className={styles.mcGlanceConstraint}>{subdomainBlockingLabel(row)}</span>
                  </button>
                  {expanded ? (
                    <div className={styles.missionExpand}>
                      <FramingLines {...framing} />
                      <div className={styles.embeddedListLabel}>Capability</div>
                      <div className={styles.diagVgrBars}>
                        {row.vgrComponents.map((comp) => {
                          const pct = vgrComponentFillPct(comp.actual, comp.ideal);
                          const tone = pct >= 80 ? 'good' : pct >= 55 ? 'mid' : 'weak';
                          return (
                            <div key={comp.key} className={styles.diagVgrBarRow}>
                              <span className={styles.diagVgrBarLabel}>{comp.label}</span>
                              {comp.ideal == null ? (
                                <span className={styles.diagVgrBarValue}>{comp.displayActual} no target</span>
                              ) : (
                                <>
                              <div className={styles.mcProgressWrap}>
                                <div className={styles.mcProgressTrack}>
                                  <div
                                    className={`${styles.mcProgressFill} ${styles[`mcProgressFill_${tone}`]}`}
                                    style={{ width: `${pct}%` }}
                                  />
                                </div>
                              </div>
                              <span className={styles.diagVgrBarValue}>
                                {comp.displayActual} / {comp.displayIdeal}
                              </span>
                                </>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      {(() => {
                        const series = deriveVgrHistory(row, vm);
                        const showTrend = trendScopeId === row.scopeId;
                        return (
                          <>
                            <button
                              type="button"
                              onClick={() =>
                                setTrendScopeId((current) => (current === row.scopeId ? null : row.scopeId))
                              }
                            >
                              {showTrend ? 'Hide trend' : 'Trend'}
                            </button>
                            {showTrend ? (
                              series.points.length > 0 ? (
                                <DiagnoseVgrHistoryChart series={series} />
                              ) : (
                                <NotAssessed label="VGR and entropy history" />
                              )
                            ) : null}
                          </>
                        );
                      })()}
                      {onNavigate ? (
                        <>
                      <button type="button" onClick={() => onNavigate('guidance')}>
                        Guidance critical path
                      </button>
                      <button type="button" onClick={() => onNavigate('drive')}>
                        Drive constraint step
                      </button>
                        </>
                      ) : null}
                      {row.issues.length > 0 ? (
                        <>
                          <div className={styles.embeddedListLabel}>Issues in this subdomain</div>
                          {row.issues.map((item) => (
                            <div key={item.itemId} className={styles.embeddedListItem}>
                              <span className={styles.embeddedListTitle}>{item.title}</span>
                            </div>
                          ))}
                        </>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </section>

      {primaryConstraint ? (
        <section aria-label="Primary constraint">
          <SectionLabel>Primary constraint</SectionLabel>
          <div className={styles.diagPrimaryConstraint}>
            <div className={styles.diagPrimaryTitle}>{primaryConstraint.title}</div>
            <div className={styles.diagPrimaryMeta}>{primaryConstraint.scopeLabel}</div>
            <p className={styles.diagPrimaryBody}>{primaryConstraint.unlockLine}</p>
          </div>
        </section>
      ) : null}

      <section aria-label="Personal load and leverage">
        <SectionLabel>You — load &amp; leverage</SectionLabel>
        <p className={styles.diagYouFraming}>Personal load and leverage — not surveillance.</p>
        {personalMetrics.showMetricsStrip ? (
          <div className={styles.diagYouStrip}>
            <div className={styles.diagYouMetric}>
              <span className={styles.diagYouMetricLabel}>Decision latency</span>
              <span className={styles.diagYouMetricValue}>{personalMetrics.decisionLatency}</span>
            </div>
            {personalMetrics.blockersCleared != null ? (
              <div className={styles.diagYouMetric}>
                <span className={styles.diagYouMetricLabel}>Blockers cleared</span>
                <span className={styles.diagYouMetricValue}>{personalMetrics.blockersCleared}</span>
              </div>
            ) : null}
            <div className={styles.diagYouMetric}>
              <span className={styles.diagYouMetricLabel}>Time in execution</span>
              <span className={styles.diagYouMetricValue}>{personalMetrics.timeInExecution}</span>
            </div>
          </div>
        ) : (
          <NotAssessed label="Personal load metrics" />
        )}
      </section>

      <section aria-label="Resource position">
        <SectionLabel>Resource position</SectionLabel>
        {resourcePosition.length === 0 ? (
          <NotAssessed label="Resource allocation across domains" />
        ) : (
          <ul data-testid="diagnose-resource-position">
            {resourcePosition.map((row) => (
              <li key={row.scopeId} className={styles.diagResourceRow}>
                <span>{row.scopeLabel}</span>
                <span className={styles.diagResourceNames}>
                  {row.count} named · {row.names.join(', ')}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

    </div>
  );
};
